import {
  DISC_GRACE_MS,
  type LegCreatePayload,
  type RtcSignalPayload,
  type StatsSnapshot,
} from "./wire.js";

export interface WebRtcManagerOptions {
  clientId: string;
  send: (type: string, payload: Record<string, unknown>) => boolean;
  onRemoteStream: (stream: MediaStream | undefined, legId?: string) => void;
  onTrackMuted?: (muted: boolean) => void;
  peerConnectionFactory?: () => RTCPeerConnection;
  mediaStreamFactory?: () => MediaStream;
}

interface LegContext {
  descriptor: LegCreatePayload;
  pairKey: string;
  connection: RTCPeerConnection;
  queuedCandidates: RTCIceCandidateInit[];
  remoteDescriptionSet: boolean;
  remoteStream?: MediaStream;
  closed: boolean;
  restartTimer?: ReturnType<typeof setTimeout>;
  statsTimer?: ReturnType<typeof setTimeout>;
}

export interface CodecLike {
  mimeType: string;
  sdpFmtpLine?: string;
}

export class WebRtcManager {
  readonly #clientId: string;
  readonly #send: WebRtcManagerOptions["send"];
  readonly #onRemoteStream: WebRtcManagerOptions["onRemoteStream"];
  readonly #onTrackMuted: NonNullable<WebRtcManagerOptions["onTrackMuted"]>;
  readonly #peerConnectionFactory: () => RTCPeerConnection;
  readonly #mediaStreamFactory: () => MediaStream;
  readonly #legs = new Map<string, LegContext>();
  readonly #pairLeg = new Map<string, string>();
  readonly #highestEpoch = new Map<string, number>();
  #localStream: MediaStream | undefined;
  #remoteStreamLegId: string | undefined;

  public constructor(options: WebRtcManagerOptions) {
    this.#clientId = options.clientId;
    this.#send = options.send;
    this.#onRemoteStream = options.onRemoteStream;
    this.#onTrackMuted = options.onTrackMuted ?? (() => undefined);
    this.#peerConnectionFactory =
      options.peerConnectionFactory ??
      (() => new RTCPeerConnection({ iceServers: [] }));
    this.#mediaStreamFactory =
      options.mediaStreamFactory ?? (() => new MediaStream());
  }

  public setLocalStream(stream: MediaStream | undefined): void {
    this.#localStream = stream;
  }

  public async handleLegCreate(descriptor: LegCreatePayload): Promise<void> {
    const pairKey = this.#pairKey(descriptor);
    const highest = this.#highestEpoch.get(pairKey) ?? 0;
    if (descriptor.epoch < highest) return;
    const currentLegId = this.#pairLeg.get(pairKey);
    if (currentLegId === descriptor.legId) return;
    if (currentLegId !== undefined) this.#closeContext(currentLegId);
    this.#highestEpoch.set(pairKey, descriptor.epoch);

    const connection = this.#peerConnectionFactory();
    const context: LegContext = {
      descriptor,
      pairKey,
      connection,
      queuedCandidates: [],
      remoteDescriptionSet: false,
      closed: false,
    };
    this.#legs.set(descriptor.legId, context);
    this.#pairLeg.set(pairKey, descriptor.legId);
    this.#wireConnection(context);

    if (descriptor.role === "offerer") {
      try {
        if (
          this.#localStream === undefined ||
          this.#localStream.getTracks().length === 0
        ) {
          this.#sendStatus(descriptor.legId, "failed");
          this.#closeContextIfCurrent(context);
          return;
        }
        for (const track of this.#localStream.getTracks()) {
          const sender = connection.addTrack(track, this.#localStream);
          if (track.kind === "video" && descriptor.h264Only) {
            applyH264CodecPreferences(connection, sender);
          }
        }
        const offer = await connection.createOffer();
        if (!this.#isCurrentContext(context)) return;
        const prepared = descriptor.h264Only ? withH264Fallback(offer) : offer;
        await connection.setLocalDescription(prepared);
        if (!this.#isCurrentContext(context)) return;
        this.#sendSignal(
          context,
          "offer",
          connection.localDescription ?? prepared,
        );
      } catch (error) {
        this.#closeContextIfCurrent(context);
        throw error;
      }
    }
  }

  public handleLegClose(legId: string): void {
    this.#closeContext(legId);
  }

  public async handleSignal(signal: RtcSignalPayload): Promise<void> {
    if (signal.to !== this.#clientId) return;
    const context = this.#legs.get(signal.legId);
    if (
      context === undefined ||
      context.closed ||
      signal.from !== context.descriptor.peer
    )
      return;

    if (signal.kind === "ice") {
      const candidate = normalizeCandidate(signal.data);
      if (candidate === undefined) return;
      if (!context.remoteDescriptionSet) {
        context.queuedCandidates.push(candidate);
      } else {
        await context.connection.addIceCandidate(candidate);
      }
      return;
    }

    if (signal.kind === "offer" || signal.kind === "ice-restart-offer") {
      if (
        context.descriptor.role !== "answerer" ||
        !isSessionDescription(signal.data, "offer")
      ) {
        return;
      }
      try {
        await context.connection.setRemoteDescription(signal.data);
        if (!this.#isCurrentContext(context)) return;
        context.remoteDescriptionSet = true;
        if (context.descriptor.h264Only)
          applyH264ReceiverPreferences(context.connection);
        await this.#flushCandidates(context);
        if (!this.#isCurrentContext(context)) return;
        const answer = await context.connection.createAnswer();
        if (!this.#isCurrentContext(context)) return;
        const prepared = context.descriptor.h264Only
          ? withH264Fallback(answer)
          : answer;
        await context.connection.setLocalDescription(prepared);
        if (!this.#isCurrentContext(context)) return;
        this.#sendSignal(
          context,
          "answer",
          context.connection.localDescription ?? prepared,
        );
      } catch (error) {
        this.#closeContextIfCurrent(context);
        throw error;
      }
      return;
    }

    if (signal.kind === "answer") {
      if (
        context.descriptor.role !== "offerer" ||
        !isSessionDescription(signal.data, "answer")
      ) {
        return;
      }
      try {
        await context.connection.setRemoteDescription(signal.data);
        if (!this.#isCurrentContext(context)) return;
        context.remoteDescriptionSet = true;
        await this.#flushCandidates(context);
      } catch (error) {
        this.#closeContextIfCurrent(context);
        throw error;
      }
    }
  }

  public closeAll(): void {
    for (const legId of [...this.#legs.keys()]) this.#closeContext(legId);
    if (this.#remoteStreamLegId !== undefined) {
      this.#remoteStreamLegId = undefined;
      this.#onRemoteStream(undefined);
      this.#onTrackMuted(false);
    }
  }

  /** New sessions may restart host epoch counters; retain no cross-session staleness state. */
  public resetForSession(): void {
    this.closeAll();
    this.#pairLeg.clear();
    this.#highestEpoch.clear();
  }

  /** Acceptance hook: make the host observe a terminal direct failure immediately. */
  public failDirectLeg(): string | undefined {
    const context = [...this.#legs.values()].find(
      (candidate) =>
        candidate.descriptor.role === "answerer" &&
        candidate.descriptor.peer !== "host" &&
        !candidate.closed,
    );
    if (context === undefined) return undefined;
    const legId = context.descriptor.legId;
    this.#sendStatus(legId, "failed");
    this.#closeContext(legId);
    return legId;
  }

  public getLegs(): ReadonlyArray<{
    legId: string;
    epoch: number;
    role: "offerer" | "answerer";
    peer: string;
    state: RTCPeerConnectionState;
    iceState: RTCIceConnectionState;
  }> {
    return [...this.#legs.values()].map((context) => ({
      legId: context.descriptor.legId,
      epoch: context.descriptor.epoch,
      role: context.descriptor.role,
      peer: context.descriptor.peer,
      state: context.connection.connectionState,
      iceState: context.connection.iceConnectionState,
    }));
  }

  public async collectStats(): Promise<StatsSnapshot[]> {
    return Promise.all(
      [...this.#legs.values()].map((context) => collectLegStats(context)),
    );
  }

  #wireConnection(context: LegContext): void {
    const { connection, descriptor } = context;
    connection.addEventListener("icecandidate", (event) => {
      const candidate = event.candidate;
      if (
        context.closed ||
        candidate === null ||
        candidate.candidate.length === 0
      )
        return;
      const json = candidate.toJSON();
      if (typeof json.candidate !== "string" || json.candidate.length === 0)
        return;
      this.#sendSignal(context, "ice", json);
    });
    connection.addEventListener("iceconnectionstatechange", () => {
      if (context.closed) return;
      const state = connection.iceConnectionState;
      this.#sendStatus(descriptor.legId, state);
      if (state === "connected" || state === "completed") {
        this.#clearRestartTimer(context);
        this.#scheduleStats(context);
      } else if (state === "disconnected" && descriptor.role === "offerer") {
        this.#clearRestartTimer(context);
        context.restartTimer = setTimeout(() => {
          context.restartTimer = undefined;
          void this.#restartIce(context).catch(() => {
            this.#failAndCloseContextIfCurrent(context);
          });
        }, DISC_GRACE_MS);
      } else if (state === "failed" || state === "closed") {
        this.#clearRestartTimer(context);
      }
    });
    connection.addEventListener("track", (event) => {
      if (context.closed) return;
      const stream = context.remoteStream ?? this.#mediaStreamFactory();
      context.remoteStream = stream;
      const candidates = [
        ...event.streams.flatMap((candidate) => candidate.getTracks()),
        event.track,
      ];
      const existing = new Set(stream.getTracks().map((track) => track.id));
      for (const track of candidates) {
        if (existing.has(track.id)) continue;
        stream.addTrack(track);
        existing.add(track.id);
      }
      this.#remoteStreamLegId = descriptor.legId;
      event.track.addEventListener("mute", () => {
        if (this.#remoteStreamLegId === descriptor.legId)
          this.#onTrackMuted(true);
      });
      event.track.addEventListener("unmute", () => {
        if (this.#remoteStreamLegId === descriptor.legId)
          this.#onTrackMuted(false);
      });
      event.track.addEventListener("ended", () => {
        if (
          context.remoteStream
            ?.getTracks()
            .every((track) => track.readyState === "ended") === true
        ) {
          this.#clearRemoteStream(descriptor.legId);
        }
      });
      this.#onRemoteStream(stream, descriptor.legId);
    });
  }

  async #restartIce(context: LegContext): Promise<void> {
    if (
      context.closed ||
      context.connection.iceConnectionState !== "disconnected"
    )
      return;
    context.connection.restartIce();
    const offer = await context.connection.createOffer({ iceRestart: true });
    if (!this.#isCurrentContext(context)) return;
    const prepared = context.descriptor.h264Only
      ? withH264Fallback(offer)
      : offer;
    await context.connection.setLocalDescription(prepared);
    if (!this.#isCurrentContext(context)) return;
    this.#sendSignal(
      context,
      "ice-restart-offer",
      context.connection.localDescription ?? prepared,
    );
  }

  async #flushCandidates(context: LegContext): Promise<void> {
    const candidates = context.queuedCandidates.splice(0);
    for (const candidate of candidates)
      await context.connection.addIceCandidate(candidate);
  }

  #sendSignal(
    context: LegContext,
    kind: RtcSignalPayload["kind"],
    data: RTCSessionDescriptionInit | RTCIceCandidateInit,
  ): void {
    this.#send("rtc-signal", {
      legId: context.descriptor.legId,
      to: context.descriptor.peer,
      kind,
      data,
    });
  }

  #sendStatus(legId: string, state: RTCIceConnectionState): void {
    this.#send("leg-status", { legId, state });
  }

  #pairKey(descriptor: LegCreatePayload): string {
    return descriptor.role === "offerer"
      ? `${this.#clientId}\u0000${descriptor.peer}`
      : `${descriptor.peer}\u0000${this.#clientId}`;
  }

  #closeContext(legId: string): void {
    const context = this.#legs.get(legId);
    if (context === undefined) return;
    context.closed = true;
    this.#clearRestartTimer(context);
    if (context.statsTimer !== undefined) clearTimeout(context.statsTimer);
    context.connection.close();
    this.#legs.delete(legId);
    if (this.#pairLeg.get(context.pairKey) === legId)
      this.#pairLeg.delete(context.pairKey);
    this.#clearRemoteStream(legId);
  }

  #isCurrentContext(context: LegContext): boolean {
    const legId = context.descriptor.legId;
    return (
      !context.closed &&
      this.#legs.get(legId) === context &&
      this.#pairLeg.get(context.pairKey) === legId
    );
  }

  #closeContextIfCurrent(context: LegContext): void {
    if (this.#isCurrentContext(context))
      this.#closeContext(context.descriptor.legId);
  }

  #failAndCloseContextIfCurrent(context: LegContext): void {
    if (!this.#isCurrentContext(context)) return;
    this.#sendStatus(context.descriptor.legId, "failed");
    this.#closeContext(context.descriptor.legId);
  }

  #clearRemoteStream(legId: string): void {
    if (this.#remoteStreamLegId !== legId) return;
    this.#remoteStreamLegId = undefined;
    this.#onRemoteStream(undefined, legId);
    this.#onTrackMuted(false);
  }

  #clearRestartTimer(context: LegContext): void {
    if (context.restartTimer !== undefined) clearTimeout(context.restartTimer);
    context.restartTimer = undefined;
  }

  #scheduleStats(context: LegContext): void {
    if (context.statsTimer !== undefined || context.closed) return;
    context.statsTimer = setTimeout(() => {
      context.statsTimer = undefined;
      void collectLegStats(context)
        .then((stats) => {
          if (context.closed) return;
          this.#send("stats-report", {
            legId: stats.legId,
            candidatePairType: stats.candidatePairType,
            rttMs: stats.rttMs,
            bytesReceived: stats.bytesReceived,
            framesDecoded: stats.framesDecoded,
            freezeCount: stats.freezeCount,
          });
          this.#scheduleStats(context);
        })
        .catch(() => {
          // A close racing getStats is terminal telemetry noise, not a leg error.
        });
    }, 10_000);
  }
}

export function prioritizeH264Codecs<T extends CodecLike>(
  codecs: readonly T[],
): T[] {
  const h264 = codecs.filter(
    (codec) => codec.mimeType.toLowerCase() === "video/h264",
  );
  const constrainedPacketized = h264.filter((codec) => {
    return isConstrainedBaselinePacketizedFmtp(codec.sdpFmtpLine ?? "");
  });
  if (h264.length > 0 && constrainedPacketized.length === 0)
    throw new Error(
      "H264-only leg has no constrained-baseline packetization-mode=1 codec",
    );
  const remaining = h264.filter(
    (codec) => !constrainedPacketized.includes(codec),
  );
  return [...constrainedPacketized, ...remaining];
}

export function withH264Fallback(
  description: RTCSessionDescriptionInit,
): RTCSessionDescriptionInit {
  if (description.sdp === undefined)
    throw new Error("H264-only leg received a description without SDP");
  return { ...description, sdp: filterVideoSdpToH264(description.sdp) };
}

export function filterVideoSdpToH264(sdp: string): string {
  const lines = sdp.split(/\r?\n/u);
  const videoStart = lines.findIndex((line) => line.startsWith("m=video "));
  if (videoStart < 0)
    throw new Error("H264-only leg has no video media section");
  const videoEndOffset = lines
    .slice(videoStart + 1)
    .findIndex((line) => line.startsWith("m="));
  const videoEnd =
    videoEndOffset < 0 ? lines.length : videoStart + 1 + videoEndOffset;
  const section = lines.slice(videoStart, videoEnd);
  const h264Payloads = new Set<string>();
  const constrainedBaselineCandidates = new Set<string>();
  const rtxByApt = new Map<string, string>();
  for (const line of section) {
    const h264 = /^a=rtpmap:(\d+) H264\//iu.exec(line);
    if (h264?.[1] !== undefined) h264Payloads.add(h264[1]);
    const fmtp = /^a=fmtp:(\d+)\s+(.+)$/iu.exec(line);
    if (fmtp?.[1] !== undefined && fmtp[2] !== undefined) {
      if (isConstrainedBaselinePacketizedFmtp(fmtp[2]))
        constrainedBaselineCandidates.add(fmtp[1]);
      const apt = parseFmtpParameters(fmtp[2]).get("apt");
      if (apt !== undefined) rtxByApt.set(fmtp[1], apt);
    }
  }
  if (h264Payloads.size === 0)
    throw new Error(
      "H264-only leg cannot negotiate because H264 is unavailable",
    );
  const constrainedBaseline = new Set(
    [...constrainedBaselineCandidates].filter((payload) =>
      h264Payloads.has(payload),
    ),
  );
  if (constrainedBaseline.size === 0)
    throw new Error(
      "H264-only leg cannot negotiate because constrained-baseline packetization-mode=1 is unavailable",
    );
  const keptPayloads = new Set(h264Payloads);
  for (const [rtx, apt] of rtxByApt)
    if (h264Payloads.has(apt)) keptPayloads.add(rtx);
  const mParts = section[0]?.split(" ") ?? [];
  const offeredPayloads = mParts.slice(3);
  const orderedPayloads = [
    ...offeredPayloads.filter((id) => constrainedBaseline.has(id)),
    ...offeredPayloads.filter(
      (id) => h264Payloads.has(id) && !constrainedBaseline.has(id),
    ),
    ...offeredPayloads.filter(
      (id) => keptPayloads.has(id) && !h264Payloads.has(id),
    ),
  ];
  section[0] = [...mParts.slice(0, 3), ...orderedPayloads].join(" ");
  const payloadLine = /^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)/u;
  const filtered = section.filter((line, index) => {
    if (index === 0) return true;
    const match = payloadLine.exec(line);
    return match?.[1] === undefined || keptPayloads.has(match[1]);
  });
  lines.splice(videoStart, videoEnd - videoStart, ...filtered);
  return lines.join("\r\n");
}

function isConstrainedBaselinePacketizedFmtp(fmtp: string): boolean {
  const parameters = parseFmtpParameters(fmtp);
  return (
    parameters.get("packetization-mode") === "1" &&
    /^42e0[0-9a-f]{2}$/u.test(parameters.get("profile-level-id") ?? "")
  );
}

function parseFmtpParameters(fmtp: string): ReadonlyMap<string, string> {
  const parameters = new Map<string, string>();
  for (const segment of fmtp.split(";")) {
    const separator = segment.indexOf("=");
    if (separator <= 0) continue;
    const name = segment.slice(0, separator).trim().toLowerCase();
    const value = segment
      .slice(separator + 1)
      .trim()
      .toLowerCase();
    if (name.length > 0) parameters.set(name, value);
  }
  return parameters;
}

function applyH264CodecPreferences(
  connection: RTCPeerConnection,
  sender: RTCRtpSender,
): void {
  const transceiver = connection
    .getTransceivers()
    .find((item) => item.sender === sender);
  const codecs = RTCRtpSender.getCapabilities?.("video")?.codecs;
  if (transceiver?.setCodecPreferences !== undefined && codecs !== undefined) {
    const h264 = prioritizeH264Codecs(codecs);
    if (h264.length === 0)
      throw new Error("H264-only sender has no H264 runtime codec");
    transceiver.setCodecPreferences(h264);
  }
}

function applyH264ReceiverPreferences(connection: RTCPeerConnection): void {
  const codecs = RTCRtpReceiver.getCapabilities?.("video")?.codecs;
  if (codecs === undefined) return;
  const h264 = prioritizeH264Codecs(codecs);
  if (h264.length === 0)
    throw new Error("H264-only receiver has no H264 runtime codec");
  for (const transceiver of connection.getTransceivers()) {
    if (
      transceiver.receiver.track.kind === "video" &&
      transceiver.setCodecPreferences !== undefined
    ) {
      transceiver.setCodecPreferences(h264);
    }
  }
}

function normalizeCandidate(
  data: RTCSessionDescriptionInit | RTCIceCandidateInit,
): RTCIceCandidateInit | undefined {
  const candidate = "candidate" in data ? data.candidate : undefined;
  if (typeof candidate !== "string" || candidate.length === 0) return undefined;
  const ice = data as RTCIceCandidateInit;
  return {
    candidate,
    ...(ice.sdpMid === undefined ? {} : { sdpMid: ice.sdpMid }),
    ...(ice.sdpMLineIndex === undefined
      ? {}
      : { sdpMLineIndex: ice.sdpMLineIndex }),
    ...(ice.usernameFragment === undefined
      ? {}
      : { usernameFragment: ice.usernameFragment }),
  };
}

function isSessionDescription(
  data: RTCSessionDescriptionInit | RTCIceCandidateInit,
  type: "offer" | "answer",
): data is RTCSessionDescriptionInit {
  return "type" in data && data.type === type;
}

async function collectLegStats(context: LegContext): Promise<StatsSnapshot> {
  const report = await context.connection.getStats();
  const byId = new Map<string, Record<string, unknown>>();
  report.forEach((entry) =>
    byId.set(entry.id, entry as unknown as Record<string, unknown>),
  );
  let pairType = "unknown";
  let rttMs = 0;
  let bytesReceived = 0;
  let framesDecoded = 0;
  let freezeCount = 0;
  let codec: string | undefined;
  let codecFmtp: string | undefined;
  for (const entry of byId.values()) {
    if (
      entry.type === "candidate-pair" &&
      entry.state === "succeeded" &&
      entry.nominated === true
    ) {
      const local =
        typeof entry.localCandidateId === "string"
          ? byId.get(entry.localCandidateId)
          : undefined;
      const remote =
        typeof entry.remoteCandidateId === "string"
          ? byId.get(entry.remoteCandidateId)
          : undefined;
      pairType = `${String(local?.candidateType ?? "unknown")}-${String(remote?.candidateType ?? "unknown")}`;
      if (typeof entry.currentRoundTripTime === "number")
        rttMs = entry.currentRoundTripTime * 1_000;
    }
    if (entry.type === "inbound-rtp" && entry.kind === "video") {
      if (typeof entry.bytesReceived === "number")
        bytesReceived += entry.bytesReceived;
      if (typeof entry.framesDecoded === "number")
        framesDecoded += entry.framesDecoded;
      if (typeof entry.freezeCount === "number")
        freezeCount += entry.freezeCount;
      const codecEntry =
        typeof entry.codecId === "string" ? byId.get(entry.codecId) : undefined;
      if (typeof codecEntry?.mimeType === "string") codec = codecEntry.mimeType;
      if (typeof codecEntry?.sdpFmtpLine === "string")
        codecFmtp = codecEntry.sdpFmtpLine;
    }
  }
  return {
    legId: context.descriptor.legId,
    state: context.connection.iceConnectionState,
    candidatePairType: pairType,
    rttMs,
    bytesReceived,
    framesDecoded,
    freezeCount,
    ...(codec === undefined ? {} : { codec }),
    ...(codecFmtp === undefined ? {} : { codecFmtp }),
  };
}
